/**
 * Crew window — opens from the "Meet Crew" button in HudHeader.
 * Ported faithfully from `_legacy/mc.html` lines 1660-1738 +
 * `_legacy/ws4-modals.js` `renderCrewModal` (lines 225-282).
 *
 * Layout reproduces the legacy chrome:
 *   - mc-panel-glow shell (true-blue border + brushed-steel outline + halo)
 *   - Title bar: empty 1fr left, centred [groups icon · MEET THE CREW · N MEMBERS pill],
 *     1fr right with status dot + Active label + ✕ CLOSE.
 *   - 2-col grid of crew cards. Each card has a HUMAN row (avatar + name +
 *     badge pill + role + bio) and an AGENT row (smart_toy icon + agent
 *     name + type + green pulse + description). Per-member accent colour
 *     drives the avatar, badge, and agent borders.
 *   - Footer bar with the active workshop's footer label.
 *
 * Improvement over legacy: the read-only crew display is the default
 * "MEET CREW" tab. When power-user mode is unlocked, a second
 * "MANAGE CREW" tab appears for facilitator CRUD on the auth roster.
 * (Legacy had no such mode — admin editing was inline on the panel.)
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Users, X } from "lucide-react";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { WORKSHOPS, type Workshop } from "@/content/workshops";
import {
  crewByWorkshop,
  crewFooterByWorkshop,
  type CrewMember,
} from "@/features/workshop/crew-content";
import { resolveCrew } from "@/features/workshop/overrides/workshop-override-content";
import { useEffectiveWorkshopOverride } from "@/stores/useEffectiveWorkshopOverride";
import { useWorkshopContentDefaults } from "@/stores/useWorkshopContentDefaults";
import { useWorkshopOverrides } from "@/stores/useWorkshopOverrides";
import { CrewCard } from "@/features/workshop/CrewCard";
import { useAuth } from "@/hooks/use-auth";
import { usePowerUser } from "@/stores/usePowerUser";
import { userManagement as umContent } from "@/content/ui";
import { UserManagementBody } from "@/components/shell/UserManagementModal";
import { ClientsManageBody } from "@/components/shell/ClientOnboardSection";
import { isMultiClientEnabled } from "@/env";
import { can } from "@/lib/auth/can";

interface Props {
  open: boolean;
  onClose: () => void;
}

function useActiveWorkshop(): Workshop["id"] {
  const { pathname } = useLocation();
  const match = /^\/workshop\/([^/]+)$/.exec(pathname);
  const id = match?.[1] ?? "";
  const known = WORKSHOPS.find((w) => w.id === id);
  if (known) return known.id;
  const populated = WORKSHOPS.find((w) => (crewByWorkshop[w.id] ?? []).length > 0);
  return populated?.id ?? "WS4";
}

/**
 * Crew grid + footer. The modal's title bar (with title / tabs / close)
 * is owned by `MeetCrewModal` so it can host the tabs inline when
 * power-user mode is unlocked instead of stacking a TabsList above this
 * panel's own header. The resolved crew is computed in the parent (so the
 * header count pill and this grid never disagree) and passed in.
 */
function MeetCrewGrid({ crew, wsId }: { crew: CrewMember[]; wsId: Workshop["id"] }) {
  const footer = crewFooterByWorkshop[wsId];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3.5 overflow-y-auto p-5 md:grid-cols-2">
        {crew.length === 0 ? (
          <div
            className="col-span-full flex items-center justify-center py-12 font-mono tracking-[0.3em] uppercase"
            style={{ color: "var(--text-muted)", fontSize: "var(--fs-base)" }}
          >
            No data available · Prompt XAES-AI
          </div>
        ) : (
          crew.map((m) => <CrewCard key={m.id} member={m} />)
        )}
      </div>

      {footer && (
        <div
          className="flex flex-shrink-0 items-center px-4 py-2"
          style={{ borderTop: "1px solid hsl(var(--border))" }}
        >
          <span
            className="font-mono tracking-[0.25em] uppercase"
            style={{ color: "var(--text-muted)", fontSize: "var(--fs-label)" }}
          >
            {footer}
          </span>
        </div>
      )}
    </div>
  );
}

export function MeetCrewModal({ open, onClose }: Props) {
  const isPowerUnlocked = usePowerUser((s) => s.isUnlocked);
  const { users, user } = useAuth();
  const showClientsTab =
    isPowerUnlocked && isMultiClientEnabled() && can(user, "manage:users");
  const wsId = useActiveWorkshop();
  // CREW-ROSTER — authored crew (WS4's named members) wins, else the live
  // roster; a power-user override (BFF-shared) pins who is shown per
  // workshop. Hydrate overrides when the modal opens (idempotent); the
  // selector re-renders once the record lands. Resolved once here so the
  // header count pill and the grid agree.
  const override = useEffectiveWorkshopOverride(wsId);
  useEffect(() => {
    if (open) {
      void useWorkshopOverrides.getState().hydrate();
      void useWorkshopContentDefaults.getState().hydrate();
    }
  }, [open]);
  const crew = resolveCrew(override, crewByWorkshop[wsId] ?? [], users);

  /* Title-bar atoms — pulled out so the unified bar reads top-down. */
  const closeButton = (
    <button
      type="button"
      onClick={onClose}
      aria-label="Close"
      className="flex items-center gap-1 rounded-md border px-2 py-1 font-sans font-semibold tracking-[0.08em] uppercase transition-colors"
      style={{
        fontSize: "var(--fs-base)",
        color: "var(--text-secondary)",
        borderColor: "hsl(var(--border))",
        background: "transparent",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = "var(--accent-true-blue)";
        e.currentTarget.style.borderColor = "var(--accent-true-blue)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = "var(--text-secondary)";
        e.currentTarget.style.borderColor = "hsl(var(--border))";
      }}
    >
      <X aria-hidden style={{ width: 11, height: 11 }} />
      Close
    </button>
  );

  const countPill = (
    <span
      className="rounded-md px-2 py-px font-sans font-semibold tracking-[0.08em]"
      style={{
        color: "var(--accent-true-blue)",
        border: "1px solid var(--accent-true-blue)",
        background: "rgba(var(--accent-true-blue-rgb), 0.12)",
        fontSize: "var(--fs-label)",
      }}
    >
      {crew.length} MEMBER{crew.length === 1 ? "" : "S"}
    </span>
  );

  const activePulse = (
    <>
      <span
        aria-hidden
        style={{
          display: "inline-block",
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: "var(--accent-green)",
          boxShadow: "0 0 6px var(--accent-green)",
        }}
      />
      <span
        className="font-mono tracking-[0.2em] uppercase"
        style={{ color: "var(--text-muted)", fontSize: "var(--fs-label)", marginRight: 8 }}
      >
        Active
      </span>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <PanelDialogContent
        data-testid="meet-crew-modal"
        className="border-0 bg-transparent p-0 shadow-none"
        style={{
          outline: "none",
          // Legacy `_legacy/mc.html` line 1661 anchors the MC_MEETCREW
          // panel at `position:absolute; inset:0; padding:20px 140px
          // 140px 140px` — the panel fills the MC body between the HUD
          // header (56 px) and the bottom-nav (112 px). Force both
          // width AND height so the dialog doesn't auto-collapse to
          // its content size.
          width: "calc(var(--os-vw) * 100 - 32px)",
          maxWidth: "calc(var(--os-vw) * 100 - 32px)",
          height: "calc(var(--os-vh) * 100 - 56px - 112px - 32px)",
          maxHeight: "calc(var(--os-vh) * 100 - 56px - 112px - 32px)",
        }}
      >
        <DialogTitle className="sr-only">Meet The Crew</DialogTitle>
        <DialogDescription className="sr-only">
          Crew roster for the active workshop. When power-user mode is unlocked, a second tab provides facilitator CRUD.
        </DialogDescription>
        <div
          // `panel-glow-mc` is the same chrome shell wrapping the MC
          // body in `MissionControl.tsx` — using it here makes the
          // crew modal read as the same "main box" rather than a
          // floating dialog with different framing.
          className="panel-glow-mc flex h-full w-full flex-col overflow-hidden backdrop-blur"
          style={{ background: "var(--bg-panel-alt)" }}
        >
          <Tabs defaultValue="meet" className="flex min-h-0 flex-1 flex-col">
            {/* Title bar — legacy 3-column layout (mc.html line 1664):
                  empty 1fr / centred title block / right cluster.
                When power-user mode is unlocked the empty 1fr hosts the
                tab switcher so the header stays one row tall and the
                centred title doesn't move. */}
            <div
              className="flex flex-shrink-0 items-center gap-3 px-4 py-3"
              style={{ borderBottom: "1px solid hsl(var(--border))" }}
            >
              <div className="flex flex-1 items-center">
                {isPowerUnlocked && (
                  <TabsList
                    style={{
                      background: "rgba(var(--accent-true-blue-rgb), 0.06)",
                      border: "1px solid rgba(var(--accent-true-blue-rgb), 0.2)",
                    }}
                  >
                    <TabsTrigger
                      value="meet"
                      className="font-sans text-fs-label font-semibold tracking-[0.08em] uppercase"
                    >
                      Meet Crew
                    </TabsTrigger>
                    <TabsTrigger
                      value="manage"
                      className="font-sans text-fs-label font-semibold tracking-[0.08em] uppercase"
                    >
                      {umContent.triggerLabel}
                    </TabsTrigger>
                  </TabsList>
                )}
              </div>
              <div className="flex items-center gap-2.5">
                <Users
                  className="h-4 w-4"
                  style={{ color: "var(--text-primary)" }}
                  aria-hidden
                />
                <p
                  className="font-sans font-semibold tracking-[0.08em] uppercase"
                  style={{
                    color: "var(--text-primary)",
                    fontSize: "var(--fs-heading)",
                  }}
                >
                  Meet The Crew
                </p>
                {countPill}
              </div>
              <div className="flex flex-1 items-center justify-end gap-2">
                {activePulse}
                {closeButton}
              </div>
            </div>

            <TabsContent
              value="meet"
              className="m-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
            >
              <MeetCrewGrid crew={crew} wsId={wsId} />
            </TabsContent>

            {isPowerUnlocked && (
              <TabsContent
                value="manage"
                className="m-0 min-h-0 flex-1 overflow-y-auto p-5 data-[state=inactive]:hidden"
              >
                {showClientsTab ? (
                  <Tabs defaultValue="crew" className="flex min-h-0 flex-col gap-3">
                    <TabsList
                      style={{
                        background: "rgba(var(--accent-true-blue-rgb), 0.06)",
                        border: "1px solid rgba(var(--accent-true-blue-rgb), 0.2)",
                      }}
                    >
                      <TabsTrigger
                        value="crew"
                        className="font-sans text-fs-label font-semibold tracking-[0.08em] uppercase"
                      >
                        {umContent.crewTab}
                      </TabsTrigger>
                      <TabsTrigger
                        value="clients"
                        className="font-sans text-fs-label font-semibold tracking-[0.08em] uppercase"
                      >
                        {umContent.clientsTab}
                      </TabsTrigger>
                    </TabsList>
                    <TabsContent value="crew" className="m-0 data-[state=inactive]:hidden">
                      <UserManagementBody />
                    </TabsContent>
                    <TabsContent value="clients" className="m-0 data-[state=inactive]:hidden">
                      <ClientsManageBody />
                    </TabsContent>
                  </Tabs>
                ) : (
                  <UserManagementBody />
                )}
              </TabsContent>
            )}
          </Tabs>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}
